import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import PrimeReact from "primereact/api";
import { DataTable } from "primereact/datatable";
import type {
  DataTableFilterMeta,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tag } from "primereact/tag";

import { LuCalendarRange, LuShieldCheck, LuUser } from "react-icons/lu";

import api from "../../../services/api";
import {
  DateFilter,
  Field,
  FilterStyles,
  Section,
} from "../../../components/FilterKit";

import { Container } from "./styles";

interface ILoginAudit {
  id: string;
  created_at: string;
  user_id: string | null;

  login_name: string | null;
  user_name: string | null;
  user_level_label: string | null;

  success: boolean;
  event_type: string;

  ip: string | null;
  user_agent: string | null;
  origin: string | null;
  referer: string | null;
}

type SortOrder = 1 | 0 | -1;

type FilterField =
  | "user_name"
  | "login_name"
  | "user_level_label"
  | "ip"
  | "success";

type QueryParams = {
  page: number;
  perPage: number;
  user_name?: string;
  login_name?: string;
  user_level_label?: string;
  ip?: string;
  success?: string;
  dateFrom?: string;
  dateTo?: string;
};

type AuditResponse = {
  items: ILoginAudit[];
  total: number;
  page: number;
  perPage: number;
};

function getFilterValue<T>(
  meta: DataTableFilterMeta,
  field: FilterField
): T | null {
  const raw = meta[field];
  if (!raw) return null;
  if (Array.isArray(raw)) return null;

  const data = raw as DataTableFilterMetaData;
  return (data.value as T) ?? null;
}

type BrowserKind =
  | "edge"
  | "chrome"
  | "firefox"
  | "safari"
  | "opera"
  | "unknown";
type OsKind = "windows" | "macos" | "android" | "ios" | "linux" | "unknown";
type DeviceKind = "mobile" | "desktop" | "unknown";

type UAInfo = {
  browser: BrowserKind;
  os: OsKind;
  device: DeviceKind;
};

const parseUserAgentInfo = (uaRaw: string): UAInfo => {
  const ua = (uaRaw || "").toLowerCase();

  let browser: BrowserKind = "unknown";
  if (ua.includes("edg/")) browser = "edge";
  else if (ua.includes("opr/") || ua.includes("opera")) browser = "opera";
  else if (ua.includes("chrome/")) browser = "chrome";
  else if (ua.includes("firefox/")) browser = "firefox";
  else if (ua.includes("safari/") && !ua.includes("chrome/"))
    browser = "safari";

  let os: OsKind = "unknown";
  if (ua.includes("windows nt")) os = "windows";
  else if (ua.includes("mac os")) os = "macos";
  else if (ua.includes("android")) os = "android";
  else if (ua.includes("iphone") || ua.includes("ipad")) os = "ios";
  else if (ua.includes("linux")) os = "linux";

  let device: DeviceKind = "unknown";
  const isMobile =
    ua.includes("mobile") ||
    ua.includes("android") ||
    ua.includes("iphone") ||
    ua.includes("ipad");
  device = isMobile ? "mobile" : "desktop";

  return { browser, os, device };
};

const browserLabel = (b: BrowserKind): string => {
  if (b === "edge") return "Edge";
  if (b === "chrome") return "Chrome";
  if (b === "firefox") return "Firefox";
  if (b === "safari") return "Safari";
  if (b === "opera") return "Opera";
  return "Desconhecido";
};

const osLabel = (o: OsKind): string => {
  if (o === "windows") return "Windows";
  if (o === "macos") return "macOS";
  if (o === "android") return "Android";
  if (o === "ios") return "iOS";
  if (o === "linux") return "Linux";
  return "";
};

const browserIcon = (b: BrowserKind): string => {
  if (b === "chrome") return "pi pi-chrome";
  if (b === "firefox") return "pi pi-firefox";
  if (b === "edge") return "pi pi-microsoft";
  if (b === "safari") return "pi pi-apple";
  if (b === "opera") return "pi pi-compass";
  return "pi pi-globe";
};

const browserColor = (b: BrowserKind): string => {
  if (b === "chrome") return "#1a73e8";
  if (b === "firefox") return "#e66000";
  if (b === "edge") return "#0aa0f6";
  if (b === "safari") return "#111827";
  if (b === "opera") return "#d7263d";
  return "#6b7280";
};

type TagSeverity = "success" | "info" | "warning" | "danger" | null;

const deviceBadge = (
  d: DeviceKind
): { label: string; icon: string; severity: TagSeverity } => {
  if (d === "mobile")
    return { label: "Mobile", icon: "pi pi-mobile", severity: "info" };
  if (d === "desktop")
    return { label: "Desktop", icon: "pi pi-desktop", severity: null };
  return { label: "N D", icon: "pi pi-question", severity: null };
};

const AuditLogins: React.FC = () => {
  PrimeReact.ripple = true;

  const dt = useRef<DataTable<ILoginAudit[]>>(null);

  const [items, setItems] = useState<ILoginAudit[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);

  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1);

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    user_name: { value: "", matchMode: "contains" },
    login_name: { value: "", matchMode: "contains" },
    user_level_label: { value: null, matchMode: "equals" },
    ip: { value: "", matchMode: "contains" },
    success: { value: null, matchMode: "equals" },
  });

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  const levelOptions = useMemo(
    () => [
      { label: "Administrador", value: "Administrador" },
      { label: "Assistente", value: "Assistente" },
      { label: "Advogado", value: "Advogado" },
      { label: "Cliente", value: "Cliente" },
    ],
    []
  );

  const successOptions = useMemo(
    () => [
      { label: "Sim", value: true },
      { label: "Não", value: false },
    ],
    []
  );

  const eventLabel = (eventType: string): string => {
    const v = (eventType || "").toUpperCase();

    if (v === "LOGIN_SUCCESS") return "Login efetuado";
    if (v === "LOGIN_FAILED") return "Tentativa inválida";
    if (v === "LOGOUT") return "Logout";
    if (v === "REFRESH_TOKEN") return "Token renovado";

    return eventType;
  };

  const eventBody = (rowData: ILoginAudit): React.ReactNode => {
    const label = eventLabel(rowData.event_type);

    if (rowData.event_type === "LOGIN_SUCCESS")
      return <span className="p-tag p-tag-success">{label}</span>;
    if (rowData.event_type === "LOGIN_FAILED")
      return <span className="p-tag p-tag-danger">{label}</span>;

    return <span className="p-tag">{label}</span>;
  };

  const formatDateTime = (iso: string): string => {
    if (!iso) return "";
    const d = new Date(iso);

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = String(d.getFullYear());

    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const createdAtBody = (rowData: ILoginAudit): React.ReactNode =>
    formatDateTime(rowData.created_at);

  const boolBody = (rowData: ILoginAudit): React.ReactNode => {
    if (rowData.success) {
      return <span className="p-tag p-tag-success">Sim</span>;
    }
    return <span className="p-tag p-tag-danger">Não</span>;
  };

  const userAgentBody = (rowData: ILoginAudit): React.ReactNode => {
    const ua = rowData.user_agent || "";
    if (!ua) return "";

    const info = parseUserAgentInfo(ua);
    const bLabel = browserLabel(info.browser);
    const oLabel = osLabel(info.os);
    const label = oLabel ? `${bLabel} ${oLabel}` : bLabel;

    const dev = deviceBadge(info.device);

    return (
      <span
        title={ua}
        style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
      >
        <Tag
          value={label}
          icon={browserIcon(info.browser)}
          style={{
            background: "#f3f4f6",
            color: "#111827",
            borderLeft: `4px solid ${browserColor(info.browser)}`,
          }}
        />

        {dev.severity ? (
          <Tag value={dev.label} icon={dev.icon} severity={dev.severity} />
        ) : (
          <Tag
            value={dev.label}
            icon={dev.icon}
            style={{
              background: "#f3f4f6",
              color: "#111827",
            }}
          />
        )}
      </span>
    );
  };

  const buildQuery = useCallback((): QueryParams => {
    const page = Math.floor(first / rows) + 1;

    const params: QueryParams = {
      page,
      perPage: rows,
    };

    const fUserName = getFilterValue<string>(filters, "user_name");
    const fLoginName = getFilterValue<string>(filters, "login_name");
    const fLevel = getFilterValue<string>(filters, "user_level_label");
    const fIp = getFilterValue<string>(filters, "ip");
    const fSuccess = getFilterValue<boolean>(filters, "success");

    if (fUserName && fUserName.trim()) params.user_name = fUserName.trim();
    if (fLoginName && fLoginName.trim()) params.login_name = fLoginName.trim();
    if (fLevel) params.user_level_label = fLevel;
    if (fIp && fIp.trim()) params.ip = fIp.trim();

    if (fSuccess !== null) params.success = String(fSuccess);

    if (dateFrom) params.dateFrom = dateFrom.toISOString();
    if (dateTo) params.dateTo = dateTo.toISOString();

    if (globalFilterValue.trim()) {
      const q = globalFilterValue.trim();
      params.user_name = params.user_name ?? q;
      params.login_name = params.login_name ?? q;
      params.ip = params.ip ?? q;
    }

    return params;
  }, [first, rows, filters, dateFrom, dateTo, globalFilterValue]);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const params = buildQuery();
      const response = await api.get<AuditResponse>("/audit/logins", {
        params,
      });
      setItems(response.data.items || []);
      setTotalRecords(response.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onPage = (e: DataTablePageEvent): void => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onSort = (e: DataTableSortEvent): void => {
    if (typeof e.sortField === "string") setSortField(e.sortField);
    setSortOrder((e.sortOrder ?? 0) as SortOrder);
  };

  const onFilter = (e: { filters: DataTableFilterMeta }): void => {
    setFilters(e.filters);
    setFirst(0);
  };

  const onGlobalFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setGlobalFilterValue(e.target.value);
    setFirst(0);
  };

  const header = (
    <div className="flex align-items-center export-buttons">
      <span style={{ fontWeight: 600, fontSize: 16 }}>Registros de Login</span>

      <span className="p-input-icon-left p-ml-auto" style={{ width: 320 }}>
        <i className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Buscar em nome, login, ip"
        />
      </span>
    </div>
  );

  const handleClear = (): void => {
    setGlobalFilterValue("");
    setDateFrom(null);
    setDateTo(null);
    setFilters({
      user_name: { value: "", matchMode: "contains" },
      login_name: { value: "", matchMode: "contains" },
      user_level_label: { value: null, matchMode: "equals" },
      ip: { value: "", matchMode: "contains" },
      success: { value: null, matchMode: "equals" },
    });
    setFirst(0);
  };

  const filtersBar = (
    <FilterStyles>
      <div className="card search-card is-inline">
        <div className="search-grid is-row">
          <div className="search-col">
            <Section icon={<LuUser />} title="Usuário">
              <div className="row r3">
                <Field id="user_name" label="Nome">
                  <InputText
                    id="user_name"
                    name="user_name"
                    value={getFilterValue<string>(filters, "user_name") ?? ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        user_name: {
                          value: e.target.value,
                          matchMode: "contains",
                        },
                      }))
                    }
                    placeholder="Contém"
                  />
                </Field>
                <Field id="login_name" label="Login">
                  <InputText
                    id="login_name"
                    name="login_name"
                    value={getFilterValue<string>(filters, "login_name") ?? ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        login_name: {
                          value: e.target.value,
                          matchMode: "contains",
                        },
                      }))
                    }
                    placeholder="Contém"
                  />
                </Field>
                <Field id="user_level_label" label="Nível">
                  <Dropdown
                    inputId="user_level_label"
                    name="user_level_label"
                    value={getFilterValue<string>(filters, "user_level_label")}
                    options={levelOptions}
                    onChange={(e: DropdownChangeEvent) =>
                      setFilters((prev) => ({
                        ...prev,
                        user_level_label: {
                          value: e.value ?? null,
                          matchMode: "equals",
                        },
                      }))
                    }
                    placeholder="Todos"
                    showClear
                    appendTo={document.body}
                  />
                </Field>
              </div>
            </Section>
          </div>

          <div className="search-col">
            <Section icon={<LuShieldCheck />} title="Acesso">
              <div className="row r2">
                <Field id="ip" label="IP">
                  <InputText
                    id="ip"
                    name="ip"
                    value={getFilterValue<string>(filters, "ip") ?? ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        ip: { value: e.target.value, matchMode: "contains" },
                      }))
                    }
                    placeholder="Contém"
                  />
                </Field>
                <Field id="success" label="Sucesso">
                  <Dropdown
                    inputId="success"
                    name="success"
                    value={getFilterValue<boolean>(filters, "success")}
                    options={successOptions}
                    onChange={(e: DropdownChangeEvent) =>
                      setFilters((prev) => ({
                        ...prev,
                        success: {
                          value: e.value ?? null,
                          matchMode: "equals",
                        },
                      }))
                    }
                    placeholder="Todos"
                    showClear
                    appendTo={document.body}
                  />
                </Field>
              </div>
            </Section>

          </div>

          <div className="search-col">
            <Section icon={<LuCalendarRange />} title="Período">
              <DateFilter
                id="login_date"
                label="Data"
                withMode={false}
                value={{ mode: "data", start: dateFrom, end: dateTo }}
                onChange={(next) => {
                  setDateFrom(next.start);
                  setDateTo(next.end);
                }}
              />
            </Section>
          </div>
        </div>

        <footer
          className="search-actions"
          style={{ position: "static", justifyContent: "flex-end" }}
        >
          <div className="buttons">
            <Button
              type="button"
              icon="pi pi-undo"
              label="Limpar campos"
              className="p-button-outlined p-button-secondary"
              onClick={handleClear}
            />
            <Button
              type="button"
              label="Atualizar"
              icon="pi pi-refresh"
              onClick={() => loadData()}
            />
          </div>
        </footer>
      </div>
    </FilterStyles>
  );

  return (
    <Container>
      <header>
        <div>
          <h1>Auditoria de Acessos</h1>
        </div>
      </header>

      {filtersBar}

      <div className="datatable-responsive">
        <div className="card">
          <Tooltip target=".export-buttons>button" position="bottom" />

          

          <DataTable
            ref={dt}
            value={items}
            header={header}
            dataKey="id"
            stripedRows
            className="p-datatable-sm"
            loading={loading}
            lazy
            paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            onFilter={onFilter}
            filters={filters}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
            rowsPerPageOptions={[10, 20, 50, 100]}
            emptyMessage="Nenhum registro encontrado."
          >
            <Column
              field="created_at"
              header="Data"
              sortable
              body={createdAtBody}
              style={{ width: 170 }}
            />
            <Column field="user_name" header="Nome" sortable />
            <Column
              field="login_name"
              header="Login"
              sortable
              style={{ width: 140 }}
            />
            <Column
              field="user_level_label"
              header="Nível"
              sortable
              style={{ width: 140 }}
            />
            <Column
              field="success"
              header="Sucesso"
              body={boolBody}
              style={{ width: 110 }}
            />
            <Column
              field="event_type"
              header="Evento"
              body={eventBody}
              sortable
              style={{ width: 160 }}
            />

            <Column field="ip" header="IP" sortable style={{ width: 150 }} />
            <Column
              field="user_agent"
              header="Navegador"
              body={userAgentBody}
              style={{ width: 260 }}
            />
          </DataTable>
        </div>
      </div>
    </Container>
  );
};

export default AuditLogins;
